import { createLogger } from '@svs/nova-core/log';
import * as z from 'zod/v4';

import { getBettingEventId } from './BettingEventId.js';
import type { CountryId } from './Country.js';
import { eventTransformSchema } from './Event.js';
import { getEventId } from './EventId.js';
import { type EventNumber, eventNumberSchema } from './EventNumber.js';
import {
  betMetricsTransformSchema,
  complementaryBetMetricsTransformSchema,
  createEventStatisticsKey,
  type EventIdProviders,
  providersTransformSchema,
  tioTidningarsTipsTransformSchema,
} from './EventStatistics.js';
import { EVENT_TYPE_ID_VARIANTS, type EventType, type EventTypeId, eventTypeIdEnum } from './EventType.js';
import {
  type Participant,
  type ParticipantId,
  type ParticipantPlayType,
  participantPlayTypeEnum,
  type ParticipantType,
} from './Participant.js';

const INTERNATIONAL_COUNTRY_ID = 1 as CountryId;
const INTERNATIONAL_COUNTRY = {
  countryId: INTERNATIONAL_COUNTRY_ID,
  name: 'Internationell',
  isoCode: 'INT',
};

const logger = createLogger('poolbetting.schema.sport');
const DEFAULT_EVENT_DESCRIPTION = 'Hemmalag - Bortalag';

function normalizeEventDescription(eventDescription: string | null | undefined): string {
  return eventDescription?.trim() || DEFAULT_EVENT_DESCRIPTION;
}

function getParticipantNames(eventDescription: string | undefined): { homeName: string; awayName: string } {
  const normalizedEventDescription = normalizeEventDescription(eventDescription);

  const [homeNameRaw, awayNameRaw] = normalizedEventDescription.split(/\s*-\s*/, 2);
  const homeName = homeNameRaw?.trim() || 'Hemmalag';
  const awayName = awayNameRaw?.trim() || 'Bortalag';

  return {
    homeName,
    awayName,
  };
}

function getShortName(name: string): string {
  return name.slice(0, 3);
}

function getFallbackParticipantId(
  eventNumber: EventNumber,
  type: ParticipantType,
  participants: Record<ParticipantId, Participant>
): ParticipantId {
  const usedIds = new Set(Object.keys(participants).map(Number));
  let fallbackId = (type === 'home' ? eventNumber : eventNumber + 1) as ParticipantId;

  while (usedIds.has(fallbackId)) {
    fallbackId = (fallbackId + 2) as ParticipantId;
  }

  return fallbackId;
}

function withFallbackParticipants({
  participants,
  participantIds,
  eventDescription,
  eventNumber,
  eventId,
}: {
  participants: Record<ParticipantId, Participant>;
  participantIds: [ParticipantId | undefined, ParticipantId | undefined];
  eventDescription: string | undefined;
  eventNumber: EventNumber;
  eventId: number;
}): { participants: Record<ParticipantId, Participant>; participantIds: ParticipantId[] } {
  const updatedParticipants = { ...participants };
  const [homeParticipantId, awayParticipantId] = participantIds;
  const { homeName, awayName } = getParticipantNames(eventDescription);
  let createdHomeId: ParticipantId | undefined;
  let createdAwayId: ParticipantId | undefined;

  const createFallbackParticipant = (type: ParticipantType, name: string): ParticipantId => {
    const id = getFallbackParticipantId(eventNumber, type, updatedParticipants);
    const shortName = getShortName(name);

    updatedParticipants[id] = {
      id,
      type,
      name,
      shortName,
      mediumName: shortName,
      countryId: INTERNATIONAL_COUNTRY_ID,
      isoCode: INTERNATIONAL_COUNTRY.isoCode,
    };

    logger.warn(`Missing ${type} participant, using fallback participant`, {
      eventId,
      eventNumber,
      fallbackParticipantId: id,
      fallbackParticipantName: name,
    });

    return id;
  };

  if (!homeParticipantId) {
    createdHomeId = createFallbackParticipant('home', homeName);
  }

  if (!awayParticipantId) {
    createdAwayId = createFallbackParticipant('away', awayName);
  }

  const safeHomeParticipantId = homeParticipantId ?? createdHomeId ?? createFallbackParticipant('home', homeName);
  const safeAwayParticipantId = awayParticipantId ?? createdAwayId ?? createFallbackParticipant('away', awayName);

  return {
    participants: updatedParticipants,
    participantIds: [safeHomeParticipantId, safeAwayParticipantId],
  };
}

function hasFallbackParticipants(
  participantIdsByType: Partial<Record<ParticipantType, ParticipantId>>
): participantIdsByType is Partial<Record<ParticipantType, ParticipantId>> {
  return !participantIdsByType.home || !participantIdsByType.away;
}

/**
 * Resolves inconsistencies for event type, ie h2h is mapped as 1x2 in the api but should be treated as h2h in the client.
 *
 * @param eventTypeId Numeric event id
 * @param participantPlayType Participation type
 * @returns Resolved event type
 */
function resolveEventType(eventTypeId: EventTypeId, participantPlayType: ParticipantPlayType): EventType {
  const eventType = EVENT_TYPE_ID_VARIANTS[eventTypeId];

  if (eventType === 'EVENT_1X2' && participantPlayType === 'Player') {
    return 'HEAD_TO_HEAD';
  }

  return eventType;
}

const eventObjectBaseSchema = z.object({
  participantType: participantPlayTypeEnum,
  eventNumber: eventNumberSchema,
  eventDescription: z.string().nullish(),
  cancelled: z.boolean(),
  eventComment: z.string(),
  extraInfo: z.string().nullish(),
  outcomes: z.number().nullish(),
});

/**
 * TS EventObject type
 */
export type EventObjectTransformSchema = z.infer<typeof eventObjectTransformSchema>;

// Schema when sportinfo is missing
export const minimalDrawEvent = eventObjectBaseSchema.transform(
  (result) =>
    ({
      eventObject: {
        ...result,
        eventDescription: normalizeEventDescription(result.eventDescription),
        complementaryBetMetrics: undefined,
      },
      status: 'MINIMAL',
    }) as const
);

/**
 * Zod schema for EventObject entity
 * Used to parse the response from the API into a EventObject entity
 */
export const eventObjectTransformSchema = z
  .object({
    ...eventObjectBaseSchema.shape,
    match: eventTransformSchema,
    eventTypeId: eventTypeIdEnum,
    betMetrics: betMetricsTransformSchema,
    complementaryBetMetrics: complementaryBetMetricsTransformSchema.optional(),
    tioTidningarsTips: tioTidningarsTipsTransformSchema,
    providerIds: providersTransformSchema.optional(),
    eventDescription: z.string().nullish(),
    eventNumber: eventNumberSchema,
    eventComment: z.string(),
  })
  .transform(
    ({
      match,
      betMetrics,
      eventTypeId,
      providerIds,
      eventNumber,
      eventComment,
      eventDescription,
      tioTidningarsTips,
      ...restEventObject
    }) => {
      const eventType = resolveEventType(eventTypeId, restEventObject.participantType);
      const eventId = getEventId(match.event.eventId);
      const normalizedEventDescription = normalizeEventDescription(eventDescription);

      const bettingEventId = getBettingEventId(eventId, restEventObject.participantType);

      const participantIdsByType = match.event.participants.reduce<Partial<Record<ParticipantType, ParticipantId>>>(
        (acc, participantId) => {
          const participant = match.participants[participantId];
          if (participant?.type) {
            acc[participant.type] = participantId;
          }

          return acc;
        },
        {}
      );

      const { participants, participantIds } = withFallbackParticipants({
        participants: match.participants,
        participantIds: [participantIdsByType.home, participantIdsByType.away],
        eventDescription: normalizedEventDescription,
        eventNumber,
        eventId,
      });

      const countries = hasFallbackParticipants(participantIdsByType)
        ? {
            ...match.countries,
            [INTERNATIONAL_COUNTRY.countryId]: INTERNATIONAL_COUNTRY,
          }
        : match.countries;

      const eventObject = {
        ...restEventObject,
        // eventType, // TODO: what is this needed for?
        eventId: eventId,
        eventDescription: normalizedEventDescription,
        eventNumber,
        eventComment,
        bettingEventId,
      };

      const eventTypeStatisticsKey = createEventStatisticsKey({
        eventId: eventObject.eventId,
        eventType,
        participantIds,
      });

      let eventTypeStatistics;
      if (betMetrics) {
        eventTypeStatistics = { ...betMetrics, ...tioTidningarsTips, id: eventTypeStatisticsKey, eventType };
      }

      const bettingEvent = {
        ...match.event,
        bettingEventId,
        participants: participantIds,
        externalId: providerIds ?? ({} as Record<EventIdProviders, string>),
        eventTypeStatisticsId: eventTypeStatisticsKey,
        eventDescription: normalizedEventDescription,
        eventType, // TODO: Magically handle H2H mapping if type is Player in Powerplay
      };

      return {
        status: 'COMPLETE',
        eventTypeStatistics,
        bettingEvent,
        participants,
        league: match.league,
        countries,
        eventObject,
      } as const;
    }
  );

export type BettingEvent = z.infer<typeof eventObjectTransformSchema>['bettingEvent'];
